/* eslint-disable camelcase */
import * as React from 'react'
import { FormikHelpers } from 'formik'

import {
  AccountNumber,
  Form,
  Forms,
  TIN,
  VINHullId,
  useFormsContext,
} from '@ally/forms'
import { Box, Button, upToMd } from '@ally/metronome-ui'

import { EnrollVerifyCustomerPayloadUnion } from '@ally/transmitigator'

import { useAppState } from '../../AppStateProvider'
import {
  FindAccountNumberModal,
  FindTINModal,
  FindVINModal,
} from '../../Modals'
import { useTransmit } from '../../TransmitContext'
import { useHostServices } from '../../HostServices'
import { FeatureFlag } from '../../Utils'
import log from '../../whisper'
import { InputWrapper } from '../Shared'
import {
  CIAM_CUSTOMER_MISSING_ERROR_CODES,
  VerifyCustomerInfoErrorBanner,
} from './VerifyCustomerInfoForm'

export interface VerifyCustomerAutoFormProps {
  formId: string
  status: string
  error?: Error | string
}
export interface VerifyCustomerAutoSubmitProps {
  tin: string
  accountNumber: string
  vin: string
  isAutoBusinessUser: boolean
}
export interface VerifyCustomerAutoFormPropsContext {
  error?: Error | string
  status: string
  onComplete: (payload: EnrollVerifyCustomerPayloadUnion) => void
}

const fieldRef = React.createRef<HTMLInputElement>()

export const VerifyCustomerAutoForm: React.FC<VerifyCustomerAutoFormProps> = ({
  formId,
  status,
  error,
}) => {
  const { actions } = useTransmit()
  const { analytics, featureFlags } = useHostServices()
  const { setStatus } = useFormsContext()
  const isOAAOLEDisabled: boolean = featureFlags.variation(
    FeatureFlag.DisableOAAOLE,
    false,
  )
  const { oaa_ole } = useAppState()

  /**
   *  If a user receives this error code back it means the info entered does
   *  not match any records in FIS. Instead of allowing the user to simply
   *  resubmit the form, transmit terminates the journey entirely. This
   *  requires us to restart the journey to allow the user to retry, but it
   *  does not come without headaches of its own.
   */
  React.useEffect(() => {
    if (error && CIAM_CUSTOMER_MISSING_ERROR_CODES.includes(error.toString())) {
      log.debug({
        message: 'User failed submit (Auto business), restarting OLE journey',
      })

      // Use status to retain the CIAM error code for after the journey restart
      setStatus(error)

      actions.onlineEnrollment({
        journey_version: 'v2',
        ...(isOAAOLEDisabled ? {} : { oaa_ole }),
      })
    }
  }, [error])

  React.useEffect(() => {
    analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Set Up Online Business Access',
    })
  }, [])

  React.useEffect(() => {
    fieldRef.current?.focus?.()
  }, [])

  return (
    <Form id={formId}>
      <VerifyCustomerInfoErrorBanner error={error} />

      <Box mb="lg">
        <InputWrapper mb="md">
          <TIN name="tin" label="TIN" required ref={fieldRef} />
        </InputWrapper>
        <Box
          mt={upToMd<string, string>('none', 'md')}
          mb={upToMd<string, string>('md', 'none')}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          marginLeft="none"
        >
          <FindTINModal />
        </Box>
      </Box>

      <Box mb="lg">
        <InputWrapper mb="md">
          <AccountNumber
            data-testid="account-number-input-field"
            isPrivate
            name="accountNumber"
            required
          />
        </InputWrapper>
        <Box
          mt={upToMd<string, string>('none', 'md')}
          mb={upToMd<string, string>('md', 'none')}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          marginLeft="none"
        >
          <FindAccountNumberModal />
        </Box>
      </Box>

      <Box mb="lg">
        <InputWrapper mb="md">
          <VINHullId
            data-testid="vin-input-field"
            isPrivate
            name="vin"
            required
          />
        </InputWrapper>
        <Box
          mt={upToMd<string, string>('none', 'md')}
          mb={upToMd<string, string>('md', 'none')}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          marginLeft="none"
        >
          <FindVINModal />
        </Box>
      </Box>

      <Button
        text="Continue"
        type="submit"
        variant="primary"
        loading={status === 'Loading'}
        fullWidthMd
        allytmln="continue"
      />
    </Form>
  )
}

export const VerifyCustomerAutoFormContext: React.FC<VerifyCustomerAutoFormPropsContext> = ({
  error,
  status,
  onComplete,
}) => {
  const formId = 'verify-customer-auto-form'

  const initialAutoFormValues: VerifyCustomerAutoSubmitProps = {
    tin: '',
    accountNumber: '',
    vin: '',
    isAutoBusinessUser: true,
  }

  const handleSubmit = (
    formValues: VerifyCustomerAutoSubmitProps,
    { setSubmitting, setStatus }: FormikHelpers<VerifyCustomerAutoSubmitProps>,
  ): void => {
    const {
      tin,
      accountNumber,
      vin,
      isAutoBusinessUser,
    } = formValues as VerifyCustomerAutoSubmitProps
    onComplete({
      tin,
      accountNumber,
      vin,
      isAutoBusinessUser,
    } as EnrollVerifyCustomerPayloadUnion)

    // Since onComplete does not return a promise, we need to immediately reset the
    // isSubmitting flag in order to be able to resubmit the form in case of error
    setSubmitting(false)
    setStatus(undefined) // reset any possible error stored in formiks status
  }
  return (
    <Forms
      formId={formId}
      initialValues={initialAutoFormValues}
      onSubmit={handleSubmit}
    >
      <VerifyCustomerAutoForm formId={formId} error={error} status={status} />
    </Forms>
  )
}
